// SPDX-License-Identifier: AGPL-3.0-or-later

import {ChannelHeaderIcon} from '@app/features/channel/components/channel_header_components/ChannelHeaderIcon';
import {CompanionAudioControls} from '@app/features/companion/components/CompanionAudioControls';
import styles from '@app/features/companion/components/CompanionPanel.module.css';
import {CompanionVoicePreview} from '@app/features/companion/components/CompanionVoicePreview';
import CompanionSessionStore, {
	type CompanionPreferences,
	type CompanionSessionMode,
	type CompanionSessionStatus,
	type CompanionSpeaker,
	type CompanionSpeakerRole,
} from '@app/features/companion/state/CompanionSessionStore';
import {Button} from '@app/features/ui/button/Button';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {Input, Textarea} from '@app/features/ui/components/form/FormInput';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	ArrowLeftIcon,
	ChatsCircleIcon,
	CheckIcon,
	CircleNotchIcon,
	CopyIcon,
	LightbulbIcon,
	MicrophoneIcon,
	PauseIcon,
	PlayIcon,
	SlidersHorizontalIcon,
	SparkleIcon,
	StopIcon,
	UsersIcon,
	XIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useId, useRef, useState} from 'react';

const COPIED_FEEDBACK_DURATION_MS = 2000;

const VOICE_COMPANION_DESCRIPTOR = msg({
	message: 'AI companion',
	comment: 'Title of the guild voice companion side panel.',
});
const CLOSE_COMPANION_PANEL_DESCRIPTOR = msg({
	message: 'Close companion panel',
	comment: 'Accessible label for the button that closes the voice companion side panel.',
});
const COMPANION_STATUS_DESCRIPTOR = msg({
	message: 'Companion status',
	comment: 'Accessible label for the session status bar in the voice companion panel.',
});
const STATUS_DESCRIPTOR = msg({
	message: 'Status',
	comment: 'Small label preceding the current voice companion session status.',
});
const STARTING_DESCRIPTOR = msg({
	message: 'Starting…',
	comment: 'Voice companion status and start button label while a session is starting.',
});
const PENDING_DESCRIPTOR = msg({
	message: 'Pending',
	comment: 'Voice companion session status while the session is pending.',
});
const LISTENING_DESCRIPTOR = msg({
	message: 'Listening',
	comment: 'Voice companion session status while the session is actively listening.',
});
const ENDING_DESCRIPTOR = msg({
	message: 'Ending…',
	comment: 'Voice companion status and end button label while a session is ending.',
});
const ENDED_DESCRIPTOR = msg({
	message: 'Ended',
	comment: 'Voice companion session status after a session has ended.',
});
const FAILED_DESCRIPTOR = msg({
	message: 'Failed',
	comment: 'Voice companion session status after a session has failed.',
});
const INTERRUPTED_DESCRIPTOR = msg({
	message: 'Interrupted',
	comment: 'Voice companion session status after a session has been interrupted.',
});
const NOT_STARTED_DESCRIPTOR = msg({
	message: 'Not started',
	comment: 'Voice companion status when there is no session.',
});
const LIVE_TRANSCRIPT_DESCRIPTOR = msg({
	message: 'Live transcript',
	comment: 'Heading and accessible label for the live voice companion transcript.',
});
const EMPTY_TRANSCRIPT_DESCRIPTOR = msg({
	message: 'Transcripts will appear here once people start speaking.',
	comment: 'Empty state in the voice companion live transcript section.',
});
const SPEAKER_DESCRIPTOR = msg({
	message: 'Speaker',
	comment: 'Generic speaker label in a voice companion transcript when no role is assigned.',
});
const ME_DESCRIPTOR = msg({
	message: 'Me',
	comment: 'Speaker role and button label identifying the local user in the voice companion panel.',
});
const CUSTOMER_DESCRIPTOR = msg({
	message: 'Customer',
	comment: 'Speaker role and button label identifying the customer in the voice companion panel.',
});
const SUGGESTIONS_DESCRIPTOR = msg({
	message: 'Suggestions',
	comment: 'Heading for voice companion suggestion cards.',
});
const GET_SUGGESTION_DESCRIPTOR = msg({
	message: 'Get suggestion',
	comment: 'Button that manually requests a voice companion suggestion.',
});
const GETTING_SUGGESTION_DESCRIPTOR = msg({
	message: 'Getting suggestion…',
	comment: 'Manual suggestion button label while a suggestion is being requested.',
});
const EMPTY_SUGGESTIONS_DESCRIPTOR = msg({
	message: 'No suggestions yet. Request one when you need help.',
	comment: 'Empty state in the voice companion suggestions section.',
});
const USE_SUGGESTION_DESCRIPTOR = msg({
	message: 'Use suggestion',
	comment: 'Button that copies a voice companion suggestion for the agent to use.',
});
const USE_SUGGESTION_KIND_DESCRIPTOR = msg({
	message: 'Use suggestion ({kind})',
	comment: 'Accessible label for copying a voice companion suggestion. Preserve the kind placeholder.',
});
const COPIED_DESCRIPTOR = msg({
	message: 'Copied',
	comment: 'Inline feedback shown only after a voice companion suggestion is copied successfully.',
});
const DISMISS_DESCRIPTOR = msg({
	message: 'Dismiss',
	comment: 'Button that dismisses a voice companion suggestion.',
});
const DISMISS_SUGGESTION_DESCRIPTOR = msg({
	message: 'Dismiss {kind} suggestion',
	comment: 'Accessible label for dismissing a voice companion suggestion. Preserve the kind placeholder.',
});
const SPEAKERS_DESCRIPTOR = msg({
	message: 'Speakers',
	comment: 'Heading for the voice companion speaker role assignment section.',
});
const EMPTY_SPEAKERS_DESCRIPTOR = msg({
	message: 'Speakers will appear here when they join the session.',
	comment: 'Empty state in the voice companion speakers section.',
});
const UNASSIGNED_DESCRIPTOR = msg({
	message: 'Unassigned',
	comment: 'Speaker role label when neither Me nor Customer has been assigned.',
});
const SPEAKER_ROLE_GROUP_DESCRIPTOR = msg({
	message: 'Speaker role for {speakerId}',
	comment: 'Accessible group label for speaker role actions. Preserve the speakerId placeholder.',
});
const MARK_AS_ME_DESCRIPTOR = msg({
	message: 'Mark {speakerId} as me',
	comment: 'Accessible label for assigning the Me role to a speaker. Preserve the speakerId placeholder.',
});
const MARK_AS_CUSTOMER_DESCRIPTOR = msg({
	message: 'Mark {speakerId} as customer',
	comment: 'Accessible label for assigning the Customer role to a speaker. Preserve the speakerId placeholder.',
});
const SCENE_DESCRIPTOR = msg({
	message: 'Scene',
	comment: 'Label for the voice companion scene selector.',
});
const MODE_ASSIST_DESCRIPTOR = msg({
	message: 'Assist me',
	comment: 'Voice companion mode option where the AI assists the user with suggestions.',
});
const MODE_TALK_DESCRIPTOR = msg({
	message: 'AI chat',
	comment: 'Voice companion mode option where the AI chats with the user on its own.',
});
const MODE_ASSIST_DESC_DESCRIPTOR = msg({
	message: 'Listens to you and suggests replies in real time.',
	comment: 'Description on the voice companion Assist me mode card.',
});
const MODE_TALK_DESC_DESCRIPTOR = msg({
	message: 'The AI talks with you and keeps you company.',
	comment: 'Description on the voice companion AI chat mode card.',
});
const CHOOSE_MODE_DESCRIPTOR = msg({
	message: 'Choose a companion mode',
	comment: 'Heading and accessible label for the voice companion mode selection cards.',
});

const BACK_TO_MODE_DESCRIPTOR = msg({
	message: 'Back to mode selection',
	comment: 'Accessible label for the button that returns to the voice companion mode selection cards.',
});
const SELECT_SCENE_DESCRIPTOR = msg({
	message: 'Select a scene',
	comment: 'Placeholder option in the voice companion scene selector.',
});
const LOADING_SCENES_DESCRIPTOR = msg({
	message: 'Loading scenes…',
	comment: 'Placeholder option while voice companion scenes are loading.',
});
const NO_SCENES_DESCRIPTOR = msg({
	message: 'No scenes available',
	comment: 'Placeholder option and empty state when no voice companion scenes are available.',
});

const START_COMPANION_DESCRIPTOR = msg({
	message: 'Start',
	comment: 'Bottom button that starts a voice companion session.',
});
const END_COMPANION_DESCRIPTOR = msg({
	message: 'End companion',
	comment: 'Bottom button that ends the active voice companion session.',
});
const AI_SPEAKER_DESCRIPTOR = msg({
	message: 'AI',
	comment: 'Speaker label for the AI companion in talk mode transcripts.',
});

const TEXT_COMPOSER_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Type to talk',
	comment: 'Placeholder of the text input for talking to the AI companion by typing.',
});
const SESSION_SUMMARY_DESCRIPTOR = msg({
	message: 'Session summary',
	comment: 'Heading of the card that shows the generated summary after a companion session ends.',
});
const COPY_SUMMARY_DESCRIPTOR = msg({
	message: 'Copy summary',
	comment: 'Button that copies the generated summary of an ended companion session.',
});
const SUGGESTION_KIND_ASSIST_DESCRIPTOR = msg({
	message: 'Assist',
	comment: 'Label for the Assist companion suggestion kind.',
});
const SUGGESTION_KIND_WHAT_TO_SAY_DESCRIPTOR = msg({
	message: 'What to say',
	comment: 'Label for the WhatToSay companion suggestion kind.',
});
const SUGGESTION_KIND_FOLLOW_UP_DESCRIPTOR = msg({
	message: 'Follow-up',
	comment: 'Label for the FollowUp companion suggestion kind.',
});
const SUGGESTION_KIND_SHORTEN_DESCRIPTOR = msg({
	message: 'Shorten',
	comment: 'Label for the Shorten companion suggestion kind.',
});
const SUGGESTION_KIND_RECAP_DESCRIPTOR = msg({
	message: 'Recap',
	comment: 'Label for the Recap companion suggestion kind.',
});
const SUGGESTION_KIND_ASK_QUESTION_DESCRIPTOR = msg({
	message: 'Ask a question',
	comment: 'Label for the AskQuestion companion suggestion kind.',
});
const SUGGESTION_KIND_MEETING_SUMMARY_DESCRIPTOR = msg({
	message: 'Meeting summary',
	comment: 'Label for the MeetingSummary companion suggestion kind.',
});
const AI_PREFERENCES_DESCRIPTOR = msg({
	message: 'AI preferences',
	comment: 'Accessible label and heading for personal AI reply style preferences in the companion panel.',
});
const TONE_LABEL_DESCRIPTOR = msg({
	message: 'Reply tone',
	comment: 'Label for selecting AI reply tone in companion preferences.',
});
const TONE_NATURAL_DESCRIPTOR = msg({
	message: 'Natural',
	comment: 'Natural AI reply tone option.',
});
const TONE_PLAYFUL_DESCRIPTOR = msg({
	message: 'Playful',
	comment: 'Playful AI reply tone option.',
});
const TONE_CALM_DESCRIPTOR = msg({
	message: 'Calm',
	comment: 'Calm AI reply tone option.',
});
const LENGTH_LABEL_DESCRIPTOR = msg({
	message: 'Reply length',
	comment: 'Label for selecting AI reply length in companion preferences.',
});
const LENGTH_BRIEF_DESCRIPTOR = msg({
	message: 'Brief',
	comment: 'Brief AI reply length option.',
});
const LENGTH_STANDARD_DESCRIPTOR = msg({
	message: 'Standard',
	comment: 'Standard AI reply length option.',
});
const NICKNAME_LABEL_DESCRIPTOR = msg({
	message: 'Preferred nickname',
	comment: 'Label for preferred nickname input in companion preferences.',
});
const NICKNAME_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'How should AI refer to you or the other person?',
	comment: 'Placeholder for preferred nickname input in companion preferences.',
});
const BACKGROUND_LABEL_DESCRIPTOR = msg({
	message: 'Personal style & context',
	comment: 'Label for personal background and style notes in companion preferences.',
});
const BACKGROUND_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Your speaking habits, game role, or context notes…',
	comment: 'Placeholder for personal background textarea in companion preferences.',
});
const AUTO_SUGGEST_LABEL_DESCRIPTOR = msg({
	message: 'Automatic suggestions',
	comment: 'Switch label enabling automatic AI suggestions on questions and trigger phrases.',
});
const SAVE_PREFERENCES_DESCRIPTOR = msg({
	message: 'Save preferences',
	comment: 'Button that saves personal AI preferences in the companion panel.',
});
const SAVING_PREFERENCES_DESCRIPTOR = msg({
	message: 'Saving…',
	comment: 'Button label while personal AI preferences are saving.',
});
const ASK_QUESTION_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Ask AI about this conversation…',
	comment: 'Placeholder for asking a free-form question during an active assist session.',
});
const ASK_BUTTON_DESCRIPTOR = msg({
	message: 'Ask',
	comment: 'Submit button for asking a free-form question during an active assist session.',
});
const GENERATING_SUGGESTION_DESCRIPTOR = msg({
	message: 'Generating…',
	comment: 'Streaming badge shown on an in-progress companion suggestion card.',
});
const SHORTEN_SUGGESTION_KIND_DESCRIPTOR = msg({
	message: 'Shorten {kind} suggestion',
	comment: 'Accessible label for shortening a specific companion suggestion card. Preserve the kind placeholder.',
});

const SESSION_STATUS_DESCRIPTORS = {
	pending: PENDING_DESCRIPTOR,
	starting: STARTING_DESCRIPTOR,
	listening: LISTENING_DESCRIPTOR,
	paused: msg({message: 'Paused'}),
	ending: ENDING_DESCRIPTOR,
	ended: ENDED_DESCRIPTOR,
	failed: FAILED_DESCRIPTOR,
	interrupted: INTERRUPTED_DESCRIPTOR,
} satisfies Record<CompanionSessionStatus, MessageDescriptor>;

const SESSION_MODE_DESCRIPTORS = {
	assist: MODE_ASSIST_DESCRIPTOR,
	talk: MODE_TALK_DESCRIPTOR,
} satisfies Record<CompanionSessionMode, MessageDescriptor>;

const SUGGESTION_KIND_LABELS: Record<string, MessageDescriptor> = {
	Assist: SUGGESTION_KIND_ASSIST_DESCRIPTOR,
	WhatToSay: SUGGESTION_KIND_WHAT_TO_SAY_DESCRIPTOR,
	FollowUp: SUGGESTION_KIND_FOLLOW_UP_DESCRIPTOR,
	Shorten: SUGGESTION_KIND_SHORTEN_DESCRIPTOR,
	Recap: SUGGESTION_KIND_RECAP_DESCRIPTOR,
	AskQuestion: SUGGESTION_KIND_ASK_QUESTION_DESCRIPTOR,
	MeetingSummary: SUGGESTION_KIND_MEETING_SUMMARY_DESCRIPTOR,
};

interface CompanionPanelProps {
	connectedChannelId?: string | null;
	connectedChannelName?: string;
}

export const CompanionPanel = observer(function CompanionPanel({
	connectedChannelId = null,
	connectedChannelName,
}: CompanionPanelProps) {
	const {i18n} = useLingui();
	const headingId = useId();
	const sceneSelectId = useId();
	const transcriptListRef = useRef<HTMLDivElement>(null);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const copyRequestRef = useRef(0);
	const summaryCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const summaryCopyRequestRef = useRef(0);
	const [isSummaryCopied, setIsSummaryCopied] = useState(false);
	const [copiedSuggestionId, setCopiedSuggestionId] = useState<string | null>(null);
	const [isStartSubmitting, setIsStartSubmitting] = useState(false);
	const [isStopSubmitting, setIsStopSubmitting] = useState(false);
	const isPanelOpen = CompanionSessionStore.isPanelOpen;
	const [textDraft, setTextDraft] = useState('');
	const [questionDraft, setQuestionDraft] = useState('');
	const [prefsDraft, setPrefsDraft] = useState<CompanionPreferences>(CompanionSessionStore.preferences);
	const transcriptCount = CompanionSessionStore.transcripts.length;
	const lastTranscriptSegmentId = CompanionSessionStore.transcripts[transcriptCount - 1]?.segment_id;

	useEffect(() => {
		setPrefsDraft(CompanionSessionStore.preferences);
	}, [
		CompanionSessionStore.preferences.tone,
		CompanionSessionStore.preferences.length,
		CompanionSessionStore.preferences.nickname,
		CompanionSessionStore.preferences.background,
		CompanionSessionStore.preferences.auto_suggest,
	]);

	useEffect(() => {
		if (!isPanelOpen) return;
		void CompanionSessionStore.loadScenes().catch(() => false);
	}, [isPanelOpen, CompanionSessionStore.selectedMode]);

	useEffect(() => {
		const transcriptList = transcriptListRef.current;
		if (!transcriptList) return;
		transcriptList.scrollTop = transcriptList.scrollHeight;
	}, [isPanelOpen, lastTranscriptSegmentId, transcriptCount]);

	useEffect(
		() => () => {
			copyRequestRef.current += 1;
			clearTimeout(copyTimeoutRef.current ?? undefined);
			summaryCopyRequestRef.current += 1;
			clearTimeout(summaryCopyTimeoutRef.current ?? undefined);
		},
		[],
	);

	const handleUseSuggestion = useCallback(
		(suggestionId: string, text: string) => {
			const requestId = ++copyRequestRef.current;
			if (copyTimeoutRef.current !== null) {
				clearTimeout(copyTimeoutRef.current);
				copyTimeoutRef.current = null;
			}
			setCopiedSuggestionId(null);
			void TextCopyCommands.copy(i18n, text, true)
				.then((copied) => {
					if (!copied || requestId !== copyRequestRef.current) return;
					setCopiedSuggestionId(suggestionId);
					copyTimeoutRef.current = setTimeout(() => {
						setCopiedSuggestionId((currentId) => (currentId === suggestionId ? null : currentId));
						copyTimeoutRef.current = null;
					}, COPIED_FEEDBACK_DURATION_MS);
				})
				.catch(() => {
					if (requestId === copyRequestRef.current) setCopiedSuggestionId(null);
				});
		},
		[i18n],
	);

	const handleCopySummary = useCallback(
		(text: string) => {
			const requestId = ++summaryCopyRequestRef.current;
			if (summaryCopyTimeoutRef.current !== null) {
				clearTimeout(summaryCopyTimeoutRef.current);
				summaryCopyTimeoutRef.current = null;
			}
			setIsSummaryCopied(false);
			void TextCopyCommands.copy(i18n, text, true)
				.then((copied) => {
					if (!copied || requestId !== summaryCopyRequestRef.current) return;
					setIsSummaryCopied(true);
					summaryCopyTimeoutRef.current = setTimeout(() => {
						setIsSummaryCopied(false);
						summaryCopyTimeoutRef.current = null;
					}, COPIED_FEEDBACK_DURATION_MS);
				})
				.catch(() => {
					if (requestId === summaryCopyRequestRef.current) setIsSummaryCopied(false);
				});
		},
		[i18n],
	);

	const handleStart = useCallback(() => {
		if (isStartSubmitting || CompanionSessionStore.isStarting || CompanionSessionStore.hasActiveSession) {
			return;
		}
		setIsStartSubmitting(true);
		const sceneId = CompanionSessionStore.selectedSceneId ?? CompanionSessionStore.defaultSceneId ?? undefined;
		void CompanionSessionStore.start(CompanionSessionStore.selectedAudioSource, sceneId)
			.catch(() => false)
			.finally(() => setIsStartSubmitting(false));
	}, [isStartSubmitting]);

	const handleStop = useCallback(() => {
		if (isStopSubmitting || CompanionSessionStore.isStopping || !CompanionSessionStore.canControlSession) return;
		setIsStopSubmitting(true);
		void CompanionSessionStore.stop()
			.catch(() => false)
			.finally(() => setIsStopSubmitting(false));
	}, [isStopSubmitting]);

	if (!isPanelOpen) return null;

	const session = CompanionSessionStore.session;
	const source =
		CompanionSessionStore.hasActiveSession && session
			? session.audio_source
			: CompanionSessionStore.selectedAudioSource;
	const roomAvailable = source.kind === 'room' && source.channel_id === connectedChannelId;
	const sessionSummary = session?.status === 'ended' ? session.summary : undefined;
	const canStart = source.kind === 'microphone' || roomAvailable;
	const canPause =
		session?.status === 'listening' ||
		(session?.status === 'paused' && (source.kind === 'microphone' || roomAvailable));
	const isStartPending = isStartSubmitting || CompanionSessionStore.isStarting || CompanionSessionStore.isRecovering;
	const isStopPending = isStopSubmitting || CompanionSessionStore.isStopping || session?.status === 'ending';
	const hasActiveSession = CompanionSessionStore.hasActiveSession;
	const canControlSession = CompanionSessionStore.canControlSession;
	const isSessionStage = hasActiveSession || isStartPending || isStopPending;
	const showSessionContent = isSessionStage || session != null;
	const statusLabel = isStartPending
		? i18n._(STARTING_DESCRIPTOR)
		: isStopPending
			? i18n._(ENDING_DESCRIPTOR)
			: session
				? i18n._(SESSION_STATUS_DESCRIPTORS[session.status])
				: i18n._(NOT_STARTED_DESCRIPTOR);
	const hasStatusWarning = session?.status === 'failed' || session?.status === 'interrupted';
	const sessionMode = session?.mode ?? CompanionSessionStore.selectedMode;
	const modeLabel = i18n._(SESSION_MODE_DESCRIPTORS[sessionMode]);
	const isStatusActive = !isStartPending && !isStopPending && session?.status === 'listening';
	const statusClassName = hasStatusWarning
		? styles.statusWarning
		: isStatusActive
			? styles.statusActive
			: isStartPending || isStopPending || session?.status === 'pending' || session?.status === 'starting'
				? styles.statusBusy
				: styles.statusIdle;

	const getSpeakerLabel = (role: CompanionSpeakerRole | 'ai' | undefined): string => {
		if (role === 'ai') return i18n._(AI_SPEAKER_DESCRIPTOR);
		if (role === 'self') return i18n._(ME_DESCRIPTOR);
		if (role === 'customer') return i18n._(CUSTOMER_DESCRIPTOR);
		return i18n._(SPEAKER_DESCRIPTOR);
	};

	const getSuggestionKindLabel = (kind: string): string => {
		const descriptor = SUGGESTION_KIND_LABELS[kind];
		return descriptor ? i18n._(descriptor) : kind;
	};

	const speakers: Array<CompanionSpeaker> = [...(session?.speakers ?? [])];
	const speakerRoles = new Map<string, CompanionSpeakerRole | undefined>(
		speakers.map((speaker) => [speaker.speaker_id, speaker.role]),
	);
	for (const transcript of CompanionSessionStore.transcripts) {
		if (speakerRoles.has(transcript.speaker_id)) continue;
		// 'ai' 仅作为字幕说话人展示, 不进入可标记 Me/Customer 的说话人列表。
		const role = transcript.role === 'ai' ? undefined : transcript.role;
		speakers.push({speaker_id: transcript.speaker_id, role});
		speakerRoles.set(transcript.speaker_id, role);
	}

	const selectedSceneId =
		CompanionSessionStore.selectedSceneId ?? session?.scene_id ?? CompanionSessionStore.defaultSceneId ?? '';
	const selectedScene = CompanionSessionStore.scenes.find((scene) => scene.id === selectedSceneId);
	const hasSelectedSceneOption = selectedScene != null;
	const scenePlaceholder = CompanionSessionStore.isLoadingScenes
		? i18n._(LOADING_SCENES_DESCRIPTOR)
		: CompanionSessionStore.scenes.length === 0
			? i18n._(NO_SCENES_DESCRIPTOR)
			: i18n._(SELECT_SCENE_DESCRIPTOR);
	const isSceneDisabled =
		isStartPending ||
		CompanionSessionStore.isSceneUpdating ||
		CompanionSessionStore.isLoadingScenes ||
		CompanionSessionStore.scenes.length === 0 ||
		(hasActiveSession && !canControlSession);
	const showModeSelectScreen = !isSessionStage && session == null && CompanionSessionStore.panelSetupStep === 'mode';
	const showSetupScreen = !isSessionStage && !showModeSelectScreen;
	const showStatusArea =
		showSessionContent || CompanionSessionStore.isDetachedSession || CompanionSessionStore.operationError != null;

	return (
		<aside className={styles.panel} aria-labelledby={headingId} data-flx="companion.companion-panel.panel">
			<header className={styles.header} data-flx="companion.companion-panel.header">
				<div className={styles.headerText} data-flx="companion.companion-panel.header-text">
					<div className={styles.titleRow} data-flx="companion.companion-panel.title-row">
						<SparkleIcon size={18} weight="fill" aria-hidden="true" data-flx="companion.companion-panel.title-icon" />
						<h2 id={headingId} className={styles.title} data-flx="companion.companion-panel.title">
							{i18n._(VOICE_COMPANION_DESCRIPTOR)}
						</h2>
					</div>
				</div>
				<div className={styles.headerActions} data-flx="companion.companion-panel.header-actions">
					<ChannelHeaderIcon
						icon={SlidersHorizontalIcon}
						iconWeight="bold"
						label={i18n._(AI_PREFERENCES_DESCRIPTOR)}
						onClick={CompanionSessionStore.togglePreferences}
						data-flx="companion.companion-panel.button.preferences"
					/>
					<ChannelHeaderIcon
						icon={XIcon}
						iconWeight="bold"
						label={i18n._(CLOSE_COMPANION_PANEL_DESCRIPTOR)}
						onClick={CompanionSessionStore.closePanel}
						data-flx="companion.companion-panel.button.close"
					/>
				</div>
			</header>

			{CompanionSessionStore.isPreferencesOpen ? (
				<section
					className={styles.preferencesSection}
					aria-label={i18n._(AI_PREFERENCES_DESCRIPTOR)}
					data-flx="companion.companion-panel.preferences-section"
				>
					<div className={styles.sectionHeader} data-flx="companion.companion-panel.preferences-header">
						<h3 className={styles.sectionTitle} data-flx="companion.companion-panel.preferences-title">
							{i18n._(AI_PREFERENCES_DESCRIPTOR)}
						</h3>
					</div>
					<Combobox
						label={i18n._(TONE_LABEL_DESCRIPTOR)}
						value={prefsDraft.tone}
						options={[
							{value: 'natural', label: i18n._(TONE_NATURAL_DESCRIPTOR)},
							{value: 'playful', label: i18n._(TONE_PLAYFUL_DESCRIPTOR)},
							{value: 'calm', label: i18n._(TONE_CALM_DESCRIPTOR)},
						]}
						onChange={(tone) => {
							if (tone === 'natural' || tone === 'playful' || tone === 'calm') {
								setPrefsDraft((prev) => ({...prev, tone}));
							}
						}}
						disabled={CompanionSessionStore.isLoadingPreferences || CompanionSessionStore.isSavingPreferences}
						isSearchable={false}
						density="compact"
						data-flx="companion.companion-panel.preferences-tone"
					/>
					<Combobox
						label={i18n._(LENGTH_LABEL_DESCRIPTOR)}
						value={prefsDraft.length}
						options={[
							{value: 'brief', label: i18n._(LENGTH_BRIEF_DESCRIPTOR)},
							{value: 'standard', label: i18n._(LENGTH_STANDARD_DESCRIPTOR)},
						]}
						onChange={(length) => {
							if (length === 'brief' || length === 'standard') {
								setPrefsDraft((prev) => ({...prev, length}));
							}
						}}
						disabled={CompanionSessionStore.isLoadingPreferences || CompanionSessionStore.isSavingPreferences}
						isSearchable={false}
						density="compact"
						data-flx="companion.companion-panel.preferences-length"
					/>
					<Input
						type="text"
						maxLength={160}
						label={i18n._(NICKNAME_LABEL_DESCRIPTOR)}
						placeholder={i18n._(NICKNAME_PLACEHOLDER_DESCRIPTOR)}
						value={prefsDraft.nickname}
						onChange={(event) => setPrefsDraft((prev) => ({...prev, nickname: event.target.value}))}
						disabled={CompanionSessionStore.isLoadingPreferences || CompanionSessionStore.isSavingPreferences}
						data-flx="companion.companion-panel.preferences-nickname"
					/>
					<Textarea
						rows={2}
						maxLength={4000}
						label={i18n._(BACKGROUND_LABEL_DESCRIPTOR)}
						placeholder={i18n._(BACKGROUND_PLACEHOLDER_DESCRIPTOR)}
						value={prefsDraft.background}
						onChange={(event) => setPrefsDraft((prev) => ({...prev, background: event.target.value}))}
						disabled={CompanionSessionStore.isLoadingPreferences || CompanionSessionStore.isSavingPreferences}
						data-flx="companion.companion-panel.preferences-background"
					/>
					<Switch
						compact
						label={i18n._(AUTO_SUGGEST_LABEL_DESCRIPTOR)}
						value={prefsDraft.auto_suggest}
						onChange={(enabled) => setPrefsDraft((prev) => ({...prev, auto_suggest: enabled}))}
						disabled={CompanionSessionStore.isLoadingPreferences || CompanionSessionStore.isSavingPreferences}
						data-flx="companion.companion-panel.preferences-auto-suggest"
					/>
					{CompanionSessionStore.preferencesError ? (
						<div className={styles.errorMessage} role="alert" data-flx="companion.companion-panel.preferences-error">
							{CompanionSessionStore.preferencesError}
						</div>
					) : null}
					<div className={styles.preferencesActions} data-flx="companion.companion-panel.preferences-actions">
						<Button
							type="button"
							variant="secondary"
							small
							fitContent
							onClick={CompanionSessionStore.closePreferences}
							data-flx="companion.companion-panel.button.close-preferences"
						>
							{i18n._(DISMISS_DESCRIPTOR)}
						</Button>
						<Button
							type="button"
							variant="primary"
							small
							fitContent
							disabled={CompanionSessionStore.isLoadingPreferences || CompanionSessionStore.isSavingPreferences}
							onClick={() => {
								void CompanionSessionStore.savePreferences(prefsDraft).then((ok) => {
									if (ok) CompanionSessionStore.closePreferences();
								});
							}}
							data-flx="companion.companion-panel.button.save-preferences"
						>
							{CompanionSessionStore.isSavingPreferences
								? i18n._(SAVING_PREFERENCES_DESCRIPTOR)
								: i18n._(SAVE_PREFERENCES_DESCRIPTOR)}
						</Button>
					</div>
				</section>
			) : null}

			{showStatusArea ? (
				<div className={styles.statusArea} data-flx="companion.companion-panel.status-area">
					{showSessionContent ? (
						<div
							className={styles.statusBar}
							role="status"
							aria-live="polite"
							aria-atomic="true"
							aria-label={i18n._(COMPANION_STATUS_DESCRIPTOR)}
							data-flx="companion.companion-panel.status-bar"
						>
							<span className={styles.statusCaption} data-flx="companion.companion-panel.status-caption">
								{i18n._(STATUS_DESCRIPTOR)}
							</span>
							<span
								className={`${styles.statusBadge} ${statusClassName}`}
								data-flx="companion.companion-panel.status-badge"
							>
								{isStartPending || isStopPending ? (
									<CircleNotchIcon
										size={14}
										className={styles.spinningIcon}
										aria-hidden="true"
										data-flx="companion.companion-panel.status-loading-icon"
									/>
								) : (
									<MicrophoneIcon size={14} aria-hidden="true" data-flx="companion.companion-panel.status-icon" />
								)}
								<span data-flx="companion.companion-panel.span">{statusLabel}</span>
							</span>
							{session ? (
								<span
									className={`${styles.statusBadge} ${styles.statusIdle}`}
									data-flx="companion.companion-panel.mode-badge"
								>
									<span data-flx="companion.companion-panel.span--2">{modeLabel}</span>
								</span>
							) : null}
						</div>
					) : null}
					{session?.mode === 'talk' &&
					session.status === 'listening' &&
					session.activity &&
					session.activity !== 'listening' ? (
						<p className={styles.aiReplyChip} data-flx="companion.companion-panel.ai-reply-chip">
							<CircleNotchIcon
								size={14}
								className={styles.spinningIcon}
								aria-hidden="true"
								data-flx="companion.companion-panel.ai-reply-icon"
							/>
							{i18n._(session?.activity === 'speaking' ? msg({message: 'Speaking…'}) : msg({message: 'Thinking…'}))}
						</p>
					) : null}
					{isSessionStage && source.kind === 'room' && !roomAvailable ? (
						<p className={styles.sceneHint} data-flx="companion.companion-panel.scene-hint--3">
							{i18n._(msg({message: 'You left this room. End companion to choose another audio source.'}))}
						</p>
					) : null}
					{CompanionSessionStore.isDetachedSession ? (
						<p className={styles.sceneHint} data-flx="companion.companion-panel.scene-hint--4">
							{i18n._(
								msg({message: 'This companion is running in another tab or device. You can pause or end it here.'}),
							)}
						</p>
					) : null}
					{session?.error ? (
						<div className={styles.errorMessage} role="alert" data-flx="companion.companion-panel.session-error">
							{session.error}
						</div>
					) : null}
					{CompanionSessionStore.operationError ? (
						<div className={styles.errorMessage} role="alert" data-flx="companion.companion-panel.operation-error">
							{CompanionSessionStore.operationError}
						</div>
					) : null}
				</div>
			) : null}

			{sessionSummary ? (
				<section
					className={styles.summaryCard}
					aria-label={i18n._(SESSION_SUMMARY_DESCRIPTOR)}
					data-flx="companion.companion-panel.summary-card"
				>
					<div className={styles.summaryHeader} data-flx="companion.companion-panel.summary-header">
						<h3 className={styles.summaryTitle} data-flx="companion.companion-panel.summary-title">
							{i18n._(SESSION_SUMMARY_DESCRIPTOR)}
						</h3>
						<Button
							variant="secondary"
							superCompact
							fitContent
							aria-label={i18n._(COPY_SUMMARY_DESCRIPTOR)}
							onClick={() => handleCopySummary(sessionSummary)}
							data-flx="companion.companion-panel.button.copy-summary"
						>
							{isSummaryCopied ? (
								<CheckIcon
									size={14}
									weight="bold"
									aria-hidden="true"
									data-flx="companion.companion-panel.summary-copied-icon"
								/>
							) : (
								<CopyIcon size={14} aria-hidden="true" data-flx="companion.companion-panel.summary-copy-icon" />
							)}
							<span data-flx="companion.companion-panel.span--6">
								{isSummaryCopied ? i18n._(COPIED_DESCRIPTOR) : i18n._(COPY_SUMMARY_DESCRIPTOR)}
							</span>
						</Button>
					</div>
					<p className={styles.summaryText} data-flx="companion.companion-panel.summary-text">
						{sessionSummary}
					</p>
				</section>
			) : null}
			{showModeSelectScreen ? (
				<section
					className={styles.modeSelectArea}
					aria-label={i18n._(CHOOSE_MODE_DESCRIPTOR)}
					data-flx="companion.companion-panel.mode-select-area"
				>
					<div className={styles.modeCards} data-flx="companion.companion-panel.mode-cards">
						<section
							className={styles.modeCard}
							aria-labelledby={`${headingId}-assist`}
							data-flx="companion.companion-panel.mode-entry-assist"
						>
							<span className={styles.modeCardIcon} data-flx="companion.companion-panel.mode-card-icon-assist">
								<LightbulbIcon size={28} aria-hidden="true" data-flx="companion.companion-panel.mode-assist-icon" />
							</span>
							<div className={styles.modeCardText} data-flx="companion.companion-panel.mode-card-text-assist">
								<h3
									id={`${headingId}-assist`}
									className={styles.modeCardTitle}
									data-flx="companion.companion-panel.mode-card-title-assist"
								>
									{i18n._(MODE_ASSIST_DESCRIPTOR)}
								</h3>
								<p
									id={`${headingId}-assist-description`}
									className={styles.modeCardDesc}
									data-flx="companion.companion-panel.mode-card-desc-assist"
								>
									{i18n._(MODE_ASSIST_DESC_DESCRIPTOR)}
								</p>
							</div>
							<Button
								variant="ghost"
								stretched
								aria-labelledby={`${headingId}-assist`}
								aria-describedby={`${headingId}-assist-description`}
								onClick={() => CompanionSessionStore.showModeSetup('assist')}
								data-flx="companion.companion-panel.mode-card-assist"
							/>
						</section>

						<section
							className={styles.modeCard}
							aria-labelledby={`${headingId}-talk`}
							data-flx="companion.companion-panel.mode-entry-talk"
						>
							<span className={styles.modeCardIcon} data-flx="companion.companion-panel.mode-card-icon-talk">
								<ChatsCircleIcon size={28} aria-hidden="true" data-flx="companion.companion-panel.mode-talk-icon" />
							</span>
							<div className={styles.modeCardText} data-flx="companion.companion-panel.mode-card-text-talk">
								<h3
									id={`${headingId}-talk`}
									className={styles.modeCardTitle}
									data-flx="companion.companion-panel.mode-card-title-talk"
								>
									{i18n._(MODE_TALK_DESCRIPTOR)}
								</h3>
								<p
									id={`${headingId}-talk-description`}
									className={styles.modeCardDesc}
									data-flx="companion.companion-panel.mode-card-desc-talk"
								>
									{i18n._(MODE_TALK_DESC_DESCRIPTOR)}
								</p>
							</div>
							<Button
								variant="ghost"
								stretched
								aria-labelledby={`${headingId}-talk`}
								aria-describedby={`${headingId}-talk-description`}
								onClick={() => CompanionSessionStore.showModeSetup('talk')}
								data-flx="companion.companion-panel.mode-card-talk"
							/>
						</section>
					</div>
				</section>
			) : null}

			{showModeSelectScreen ? null : (
				<div
					className={showSetupScreen ? styles.setupBody : styles.sessionBody}
					data-flx="companion.companion-panel.body"
				>
					{showSetupScreen ? (
						<section
							className={styles.sourceArea}
							aria-label={i18n._(msg({message: 'Audio source'}))}
							data-flx="companion.companion-panel.source-area"
						>
							{session == null ? (
								<div className={styles.setupModeRow} data-flx="companion.companion-panel.setup-mode-row">
									<ChannelHeaderIcon
										icon={ArrowLeftIcon}
										iconWeight="bold"
										label={i18n._(BACK_TO_MODE_DESCRIPTOR)}
										onClick={CompanionSessionStore.showModeSelect}
										data-flx="companion.companion-panel.button.back-to-mode"
									/>
									<span className={styles.setupModeLabel} data-flx="companion.companion-panel.setup-mode-label">
										{modeLabel}
									</span>
								</div>
							) : null}
							<div className={styles.sceneLabel} data-flx="companion.companion-panel.source-label">
								{i18n._(msg({message: 'Audio source'}))}
							</div>
							<RadioGroup<'microphone' | 'system' | 'room'>
								value={
									source.kind === 'microphone' && sessionMode === 'assist' && CompanionSessionStore.includeSystemAudio
										? 'system'
										: source.kind
								}
								aria-label={i18n._(msg({message: 'Audio source'}))}
								options={[
									{value: 'microphone', name: i18n._(msg({message: 'Personal microphone'}))},
									...(sessionMode === 'assist'
										? [
												{
													value: 'system' as const,
													name: i18n._(msg({message: 'Microphone + system audio'})),
													desc: i18n._(
														msg({
															message:
																'Include the other side from another app or tab. In the browser, enable audio sharing.',
														}),
													),
													disabled: !getElectronAPI() && typeof navigator.mediaDevices?.getDisplayMedia !== 'function',
												},
											]
										: []),
									{
										value: 'room',
										name: i18n._(msg({message: 'Room conversation'})),
										desc: connectedChannelName,
										disabled: !connectedChannelId,
									},
								]}
								onChange={(kind) => {
									CompanionSessionStore.selectSystemAudio(kind === 'system');
									if (kind === 'microphone' || kind === 'system')
										CompanionSessionStore.selectAudioSource({kind: 'microphone'});
									else if (connectedChannelId)
										CompanionSessionStore.selectAudioSource({kind, channel_id: connectedChannelId});
								}}
								data-flx="companion.companion-panel.audio-source"
							/>

							{source.kind === 'microphone' && !CompanionSessionStore.isDetachedSession ? (
								<CompanionAudioControls data-flx="companion.companion-panel.companion-audio-controls" />
							) : null}
						</section>
					) : null}
					{isSessionStage && source.kind === 'microphone' && !CompanionSessionStore.isDetachedSession ? (
						<CompanionAudioControls data-flx="companion.companion-panel.companion-audio-controls--2" />
					) : null}

					{showSessionContent ? (
						<div className={styles.content} data-flx="companion.companion-panel.content">
							<section
								className={`${styles.section} ${styles.transcriptSection}`}
								data-flx="companion.companion-panel.transcript-section"
							>
								<div className={styles.sectionHeader} data-flx="companion.companion-panel.transcript-header">
									<h3 className={styles.sectionTitle} data-flx="companion.companion-panel.transcript-title">
										{i18n._(LIVE_TRANSCRIPT_DESCRIPTOR)}
									</h3>
								</div>
								<div
									ref={transcriptListRef}
									className={styles.transcriptScroller}
									role="log"
									aria-live="polite"
									aria-relevant="additions text"
									aria-label={i18n._(LIVE_TRANSCRIPT_DESCRIPTOR)}
									data-flx="companion.companion-panel.transcript-log"
								>
									{transcriptCount === 0 ? (
										<div className={styles.emptyState} data-flx="companion.companion-panel.transcript-empty">
											{i18n._(EMPTY_TRANSCRIPT_DESCRIPTOR)}
										</div>
									) : (
										<ol className={styles.transcriptList} data-flx="companion.companion-panel.transcript-list">
											{CompanionSessionStore.transcripts.map((transcript) => {
												const role = speakerRoles.get(transcript.speaker_id) ?? transcript.role;
												return (
													<li
														key={transcript.segment_id}
														className={
															transcript.final
																? styles.transcriptItem
																: `${styles.transcriptItem} ${styles.transcriptItemPartial}`
														}
														data-flx="companion.companion-panel.transcript-item"
													>
														<div className={styles.transcriptMeta} data-flx="companion.companion-panel.transcript-meta">
															<span
																className={styles.speakerLabel}
																data-flx="companion.companion-panel.transcript-speaker-label"
															>
																{getSpeakerLabel(role)}
															</span>
															<span
																className={styles.speakerId}
																data-flx="companion.companion-panel.transcript-speaker-id"
															>
																{transcript.speaker_id}
															</span>
														</div>
														<p className={styles.transcriptText} data-flx="companion.companion-panel.transcript-text">
															{transcript.text}
														</p>
													</li>
												);
											})}
										</ol>
									)}
								</div>
							</section>

							{session?.mode !== 'talk' ? (
								<>
									<section className={styles.section} data-flx="companion.companion-panel.suggestions-section">
										<div className={styles.sectionHeader} data-flx="companion.companion-panel.suggestions-header">
											<h3 className={styles.sectionTitle} data-flx="companion.companion-panel.suggestions-title">
												{i18n._(SUGGESTIONS_DESCRIPTOR)}
											</h3>
											<Button
												variant="secondary"
												superCompact
												fitContent
												disabled={
													session?.status !== 'listening' ||
													CompanionSessionStore.isSuggesting ||
													isStartPending ||
													isStopPending
												}
												aria-busy={CompanionSessionStore.isSuggesting}
												onClick={() => void CompanionSessionStore.requestSuggestion().catch(() => false)}
												data-flx="companion.companion-panel.button.request-suggestion"
											>
												{CompanionSessionStore.isSuggesting ? (
													<CircleNotchIcon
														size={14}
														className={styles.spinningIcon}
														aria-hidden="true"
														data-flx="companion.companion-panel.suggestion-loading-icon"
													/>
												) : (
													<SparkleIcon
														size={14}
														aria-hidden="true"
														data-flx="companion.companion-panel.suggestion-icon"
													/>
												)}
												<span data-flx="companion.companion-panel.span--3">
													{CompanionSessionStore.isSuggesting
														? i18n._(GETTING_SUGGESTION_DESCRIPTOR)
														: i18n._(GET_SUGGESTION_DESCRIPTOR)}
												</span>
											</Button>
										</div>
										<div className={styles.quickActionsRow} data-flx="companion.companion-panel.quick-actions">
											{(['WhatToSay', 'FollowUp', 'Recap'] as const).map((actionKey) => (
												<Button
													key={actionKey}
													type="button"
													variant="secondary"
													superCompact
													fitContent
													disabled={
														session?.status !== 'listening' ||
														CompanionSessionStore.isSuggesting ||
														isStartPending ||
														isStopPending
													}
													onClick={() => void CompanionSessionStore.requestSuggestion(actionKey).catch(() => false)}
													data-flx={`companion.companion-panel.button.quick-${actionKey.toLowerCase()}`}
												>
													{getSuggestionKindLabel(actionKey)}
												</Button>
											))}
										</div>
										<form
											className={styles.askComposer}
											onSubmit={(event) => {
												event.preventDefault();
												const question = questionDraft.trim();
												if (!question || session?.status !== 'listening' || CompanionSessionStore.isSuggesting) return;
												setQuestionDraft('');
												void CompanionSessionStore.requestSuggestion({action: 'AskQuestion', question}).catch(
													() => false,
												);
											}}
											data-flx="companion.companion-panel.ask-composer"
										>
											<Input
												type="text"
												maxLength={500}
												value={questionDraft}
												onChange={(event) => setQuestionDraft(event.target.value)}
												placeholder={i18n._(ASK_QUESTION_PLACEHOLDER_DESCRIPTOR)}
												aria-label={i18n._(ASK_QUESTION_PLACEHOLDER_DESCRIPTOR)}
												disabled={
													session?.status !== 'listening' ||
													CompanionSessionStore.isSuggesting ||
													isStartPending ||
													isStopPending
												}
												data-flx="companion.companion-panel.ask-input"
											/>
											<Button
												type="submit"
												variant="secondary"
												small
												fitContent
												disabled={
													!questionDraft.trim() ||
													session?.status !== 'listening' ||
													CompanionSessionStore.isSuggesting ||
													isStartPending ||
													isStopPending
												}
												data-flx="companion.companion-panel.button.ask-question"
											>
												{i18n._(ASK_BUTTON_DESCRIPTOR)}
											</Button>
										</form>
										{CompanionSessionStore.suggestions.length === 0 ? (
											<div className={styles.emptyStateCompact} data-flx="companion.companion-panel.suggestions-empty">
												{CompanionSessionStore.isSuggesting
													? i18n._(GETTING_SUGGESTION_DESCRIPTOR)
													: i18n._(EMPTY_SUGGESTIONS_DESCRIPTOR)}
											</div>
										) : (
											<div className={styles.suggestionList} data-flx="companion.companion-panel.suggestion-list">
												{CompanionSessionStore.suggestions.map((suggestion) => (
													<article
														key={suggestion.suggestion_id}
														className={styles.suggestionCard}
														data-flx="companion.companion-panel.suggestion-card"
													>
														<div
															className={styles.suggestionHeaderRow}
															data-flx="companion.companion-panel.suggestion-header-row"
														>
															<div
																className={styles.suggestionKind}
																data-flx="companion.companion-panel.suggestion-kind"
															>
																{getSuggestionKindLabel(suggestion.kind)}
															</div>
															{suggestion.final === false ? (
																<span
																	className={styles.suggestionStreamingBadge}
																	role="status"
																	data-flx="companion.companion-panel.suggestion-streaming"
																>
																	<CircleNotchIcon
																		size={12}
																		className={styles.spinningIcon}
																		aria-hidden="true"
																		data-flx="companion.companion-panel.suggestion-streaming-icon"
																	/>
																	{i18n._(GENERATING_SUGGESTION_DESCRIPTOR)}
																</span>
															) : null}
														</div>
														<p className={styles.suggestionText} data-flx="companion.companion-panel.suggestion-text">
															{suggestion.text}
														</p>
														<div
															className={styles.suggestionActions}
															data-flx="companion.companion-panel.suggestion-actions"
														>
															<Button
																variant="secondary"
																superCompact
																fitContent
																aria-label={i18n._(USE_SUGGESTION_KIND_DESCRIPTOR, {
																	kind: getSuggestionKindLabel(suggestion.kind),
																})}
																onClick={() => handleUseSuggestion(suggestion.suggestion_id, suggestion.text)}
																data-flx="companion.companion-panel.button.use-suggestion"
															>
																<CopyIcon size={14} aria-hidden="true" data-flx="companion.companion-panel.copy-icon" />
																{i18n._(USE_SUGGESTION_DESCRIPTOR)}
															</Button>
															{suggestion.kind !== 'Shorten' ? (
																<Button
																	type="button"
																	variant="secondary"
																	superCompact
																	fitContent
																	disabled={
																		suggestion.final === false ||
																		session?.status !== 'listening' ||
																		CompanionSessionStore.isSuggesting
																	}
																	aria-label={i18n._(SHORTEN_SUGGESTION_KIND_DESCRIPTOR, {
																		kind: getSuggestionKindLabel(suggestion.kind),
																	})}
																	onClick={() =>
																		void CompanionSessionStore.requestSuggestion({
																			action: 'Shorten',
																			previousResponse: suggestion.text,
																		}).catch(() => false)
																	}
																	data-flx="companion.companion-panel.button.shorten-suggestion"
																>
																	{getSuggestionKindLabel('Shorten')}
																</Button>
															) : null}
															{copiedSuggestionId === suggestion.suggestion_id ? (
																<span
																	className={styles.copiedFeedback}
																	role="status"
																	data-flx="companion.companion-panel.copied-feedback"
																>
																	<CheckIcon
																		size={14}
																		weight="bold"
																		aria-hidden="true"
																		data-flx="companion.companion-panel.copied-icon"
																	/>
																	{i18n._(COPIED_DESCRIPTOR)}
																</span>
															) : null}
															<Button
																variant="ghost"
																superCompact
																fitContent
																className={styles.dismissButton}
																aria-label={i18n._(DISMISS_SUGGESTION_DESCRIPTOR, {
																	kind: getSuggestionKindLabel(suggestion.kind),
																})}
																onClick={() => CompanionSessionStore.dismissSuggestion(suggestion.suggestion_id)}
																data-flx="companion.companion-panel.button.dismiss-suggestion"
															>
																{i18n._(DISMISS_DESCRIPTOR)}
															</Button>
														</div>
													</article>
												))}
											</div>
										)}
									</section>

									<section className={styles.section} data-flx="companion.companion-panel.speakers-section">
										<div className={styles.sectionHeader} data-flx="companion.companion-panel.speakers-header">
											<h3 className={styles.sectionTitle} data-flx="companion.companion-panel.speakers-title">
												{i18n._(SPEAKERS_DESCRIPTOR)}
											</h3>
											<UsersIcon size={16} aria-hidden="true" data-flx="companion.companion-panel.speakers-icon" />
										</div>
										{speakers.length === 0 ? (
											<div className={styles.emptyStateCompact} data-flx="companion.companion-panel.speakers-empty">
												{i18n._(EMPTY_SPEAKERS_DESCRIPTOR)}
											</div>
										) : (
											<ul className={styles.speakerList} data-flx="companion.companion-panel.speaker-list">
												{speakers.map((speaker) => {
													const isPending = CompanionSessionStore.pendingSpeakerIds.has(speaker.speaker_id);
													return (
														<li
															key={speaker.speaker_id}
															className={styles.speakerItem}
															data-flx="companion.companion-panel.speaker-item"
														>
															<div
																className={styles.speakerDetails}
																data-flx="companion.companion-panel.speaker-details"
															>
																<span className={styles.speakerName} data-flx="companion.companion-panel.speaker-name">
																	{speaker.speaker_id}
																</span>
																<span className={styles.speakerRole} data-flx="companion.companion-panel.speaker-role">
																	{speaker.role ? getSpeakerLabel(speaker.role) : i18n._(UNASSIGNED_DESCRIPTOR)}
																</span>
															</div>
															<div
																className={styles.speakerActions}
																role="group"
																aria-label={i18n._(SPEAKER_ROLE_GROUP_DESCRIPTOR, {speakerId: speaker.speaker_id})}
																aria-busy={isPending}
																data-flx="companion.companion-panel.speaker-actions"
															>
																<Button
																	variant={speaker.role === 'self' ? 'primary' : 'secondary'}
																	superCompact
																	fitContent
																	aria-label={i18n._(MARK_AS_ME_DESCRIPTOR, {speakerId: speaker.speaker_id})}
																	aria-pressed={speaker.role === 'self'}
																	disabled={isPending || !canControlSession}
																	onClick={() =>
																		void CompanionSessionStore.markSpeaker(speaker.speaker_id, 'self').catch(
																			() => false,
																		)
																	}
																	data-flx="companion.companion-panel.button.mark-self"
																>
																	{i18n._(ME_DESCRIPTOR)}
																</Button>
																<Button
																	variant={speaker.role === 'customer' ? 'primary' : 'secondary'}
																	superCompact
																	fitContent
																	aria-label={i18n._(MARK_AS_CUSTOMER_DESCRIPTOR, {speakerId: speaker.speaker_id})}
																	aria-pressed={speaker.role === 'customer'}
																	disabled={isPending || !canControlSession}
																	onClick={() =>
																		void CompanionSessionStore.markSpeaker(speaker.speaker_id, 'customer').catch(
																			() => false,
																		)
																	}
																	data-flx="companion.companion-panel.button.mark-customer"
																>
																	{i18n._(CUSTOMER_DESCRIPTOR)}
																</Button>
															</div>
														</li>
													);
												})}
											</ul>
										)}
									</section>
								</>
							) : null}
						</div>
					) : null}

					{showModeSelectScreen ? null : (
						<section className={styles.section} data-flx="companion.companion-panel.scene-section">
							<Combobox
								id={sceneSelectId}
								label={i18n._(
									(session?.mode ?? CompanionSessionStore.selectedMode) === 'talk'
										? msg({message: 'Companion role'})
										: SCENE_DESCRIPTOR,
								)}
								value={selectedSceneId}
								options={[
									...(selectedSceneId && !hasSelectedSceneOption
										? [{value: selectedSceneId, label: selectedSceneId}]
										: []),
									...CompanionSessionStore.scenes.map((scene) => ({value: scene.id, label: scene.name})),
								]}
								onChange={(sceneId) => {
									if (sceneId) void CompanionSessionStore.setScene(sceneId).catch(() => false);
								}}
								disabled={isSceneDisabled}
								isLoading={CompanionSessionStore.isLoadingScenes || CompanionSessionStore.isSceneUpdating}
								isSearchable={false}
								placeholder={scenePlaceholder}
								error={CompanionSessionStore.scenesError ?? undefined}
								density="compact"
								data-flx="companion.companion-panel.scene-select"
							/>
							{(session?.mode ?? CompanionSessionStore.selectedMode) === 'talk' ? (
								<>
									{selectedScene?.description ? (
										<p className={styles.sceneHint} data-flx="companion.role-description">
											{selectedScene.description}
										</p>
									) : null}
									{!hasActiveSession ? (
										<CompanionVoicePreview
											sceneId={selectedSceneId}
											disabled={isStartPending}
											data-flx="companion.companion-panel.companion-voice-preview"
										/>
									) : null}
								</>
							) : null}
						</section>
					)}
				</div>
			)}

			{CompanionSessionStore.playbackBlocked ? (
				<Button
					variant="secondary"
					small
					fitContainer
					onClick={() => void CompanionSessionStore.resumePlayback()}
					data-flx="companion.enable-audio"
				>
					{i18n._(msg({message: 'Enable audio'}))}
				</Button>
			) : null}
			{session?.mode === 'talk' && session.status === 'listening' ? (
				<Button
					variant="ghost"
					small
					fitContainer
					onClick={() => void CompanionSessionStore.interrupt()}
					data-flx="companion.stop-speaking"
				>
					{i18n._(msg({message: 'Stop playback'}))}
				</Button>
			) : null}

			{session?.mode === 'talk' &&
			source.kind === 'microphone' &&
			['listening', 'thinking', 'speaking'].includes(session.status) ? (
				<form
					className={styles.textComposer}
					onSubmit={(event) => {
						event.preventDefault();
						const value = textDraft.trim();
						if (!value) return;
						setTextDraft('');
						void CompanionSessionStore.sendText(value);
					}}
					data-flx="companion.companion-panel.text-composer"
				>
					<Input
						type="text"
						maxLength={500}
						value={textDraft}
						onChange={(event) => setTextDraft(event.target.value)}
						placeholder={i18n._(TEXT_COMPOSER_PLACEHOLDER_DESCRIPTOR)}
						aria-label={i18n._(TEXT_COMPOSER_PLACEHOLDER_DESCRIPTOR)}
						data-flx="companion.companion-panel.text-composer-input"
					/>
					<Button
						type="submit"
						variant="primary"
						small
						fitContent
						disabled={!textDraft.trim()}
						data-flx="companion.companion-panel.button.send-text"
					>
						{i18n._(msg({message: 'Send'}))}
					</Button>
				</form>
			) : null}

			{showModeSelectScreen ? null : (
				<footer
					className={showSetupScreen ? `${styles.footer} ${styles.footerSingleAction}` : styles.footer}
					data-flx="companion.companion-panel.footer"
				>
					{showSetupScreen ? (
						<Button
							type="button"
							variant="primary"
							small
							fitContainer
							className={styles.footerButton}
							disabled={!canStart}
							onClick={handleStart}
							data-flx="companion.companion-panel.button.start"
						>
							<PlayIcon size={16} weight="fill" aria-hidden="true" data-flx="companion.companion-panel.start-icon" />
							<span data-flx="companion.companion-panel.span--4">{i18n._(START_COMPANION_DESCRIPTOR)}</span>
						</Button>
					) : (
						<Button
							type="button"
							variant="primary"
							small
							fitContainer
							className={styles.footerButton}
							disabled={isStartPending || isStopPending || CompanionSessionStore.isPausing || !canPause}
							aria-busy={isStartPending || CompanionSessionStore.isPausing}
							onClick={() => void CompanionSessionStore.setPaused(session?.status !== 'paused')}
							data-flx="companion.companion-panel.button.pause"
						>
							{isStartPending || CompanionSessionStore.isPausing ? (
								<CircleNotchIcon
									size={16}
									className={styles.spinningIcon}
									aria-hidden="true"
									data-flx="companion.companion-panel.start-loading-icon"
								/>
							) : session?.status === 'paused' ? (
								<PlayIcon size={16} weight="fill" aria-hidden="true" data-flx="companion.companion-panel.start-icon" />
							) : (
								<PauseIcon size={16} weight="fill" aria-hidden="true" data-flx="companion.companion-panel.pause-icon" />
							)}
							<span data-flx="companion.companion-panel.span--4">
								{isStartPending
									? i18n._(STARTING_DESCRIPTOR)
									: i18n._(
											session?.status === 'paused'
												? msg({message: 'Resume companion'})
												: msg({message: 'Pause companion'}),
										)}
							</span>
						</Button>
					)}
					{isSessionStage ? (
						<Button
							type="button"
							variant="secondary"
							small
							fitContainer
							className={styles.footerButton}
							disabled={!canControlSession || isStopPending || isStartPending}
							aria-busy={isStopPending}
							onClick={handleStop}
							data-flx="companion.companion-panel.button.stop"
						>
							{isStopPending ? (
								<CircleNotchIcon
									size={16}
									className={styles.spinningIcon}
									aria-hidden="true"
									data-flx="companion.companion-panel.stop-loading-icon"
								/>
							) : (
								<StopIcon size={16} weight="fill" aria-hidden="true" data-flx="companion.companion-panel.stop-icon" />
							)}
							<span data-flx="companion.companion-panel.span--5">
								{isStopPending ? i18n._(ENDING_DESCRIPTOR) : i18n._(END_COMPANION_DESCRIPTOR)}
							</span>
						</Button>
					) : null}
				</footer>
			)}
		</aside>
	);
});
